// codeRepValues Ids for Category='CheckOutRequest' are environment-specific and shift whenever
// the reference data is reseeded, so the real Id is resolved by matching the stable Code text
// (statuses is the getRefValues(Category='CheckOutRequest') list: {label, value, code}) instead
// of a hardcoded Id.
export const getCheckoutStatusIdByCode = (statuses, code) =>
  (statuses || []).find((status) => status.code === code)?.value;

export const CHECKOUT_STATUS = {
  PENDING: {
    value: 'Pending',
    color: 'primary',
  },

  REVIEWED: {
    value: 'Reviewed',
    color: 'secondary',
  },

  READY: {
    value: 'Ready',
    color: 'success',
  },

  COMPLETE: {
    value: 'Complete',
    color: 'default',
  },

  CANCEL: {
    value: 'Cancel',
    color: 'error',
  },
};

// `next` is the Code text of the status to move to — resolve its real Id at call time via
// getCheckoutStatusIdByCode(statuses, action.next), not a hardcoded Id baked in here.
export const NEXT_ACTION = {
  [CHECKOUT_STATUS.PENDING.value]: {
    label: 'Mark Reviewed',
    next: CHECKOUT_STATUS.REVIEWED.value,
    color: CHECKOUT_STATUS.REVIEWED.color,
  },

  [CHECKOUT_STATUS.REVIEWED.value]: {
    label: 'Mark Ready',
    next: CHECKOUT_STATUS.READY.value,
    color: CHECKOUT_STATUS.READY.color,
  },

  [CHECKOUT_STATUS.READY.value]: {
    label: 'Complete',
    next: CHECKOUT_STATUS.COMPLETE.value,
    color: CHECKOUT_STATUS.COMPLETE.color,
  },
};

export const DIALOG_MODE = {
  ADD: 'add',
  EDIT: 'edit',
};

export const GROUPS_MAX_HEIGHT = '75vh';

export const CHECKOUT_GROUP_BY = {
  NONE: 'none',
  LOCATION: 'location',
};

export const CHECKOUT_EMAIL_DIALOG_DEFAULT_VALUES = {
  email: '',
};

const STATUS_CODE_MAP = {
  [CHECKOUT_STATUS.COMPLETE.value]: CHECKOUT_STATUS.COMPLETE.value,
};

export const normalizeStatus = (code) => STATUS_CODE_MAP[code] || code;

export const mapCheckoutRequest = (raw) => {
  const storageEntries = raw.incEvidence?.EvidenceStorage || [];

  return {
    ...raw,
    GUID: raw.ptsEvidenceRequestID,
    RequestNum: raw.ptsEvidenceRequestID,
    ItemNum: raw.incEvidence?.EvidenceID,
    ptsEvidenceID: raw.incEvidence?.ptsEvidenceID || null,
    IncidentID: raw.incEvidence?.ptsIncidentID || null,
    Description: raw.incEvidence?.Description || '—',
    Category: raw.incEvidence?.Category || '—',
    EvidenceStatus: raw.incEvidence?.Status || '—',
    CurrentLocation: raw.incEvidence?.CurrentLocation || '—',
    TagNumber: raw.incEvidence?.TagNumber || '—',
    RequestBy: raw.RequestedByPerson?.FullName || '—',
    Location: raw.PickupLocation?.Code || '—',
    ReasonNeeded: raw.ReasonNeeded?.Code || '—',
    Status: normalizeStatus(raw.StatusDetails?.Code),
    Comments: raw.Comments || '',
    ProcessedBy: raw.ProcessedByPerson?.FullName || '—',
    StorageLocation: storageEntries[0]?.StorageLocation || '—',
    SubLocation: storageEntries[0]?.SubLocation || '—',
    Container: storageEntries[0]?.Container || '—',
    StorageLocations: [...new Set(storageEntries.map((s) => s.StorageLocation).filter(Boolean))],
    SubLocations: [...new Set(storageEntries.map((s) => s.SubLocation).filter(Boolean))],
    Containers: [...new Set(storageEntries.map((s) => s.Container).filter(Boolean))],
    StorageLocationIDs: [
      ...new Set(storageEntries.map((s) => s.StorageLocationID).filter(Boolean)),
    ],
    SubLocationIDs: [...new Set(storageEntries.map((s) => s.SubLocationID).filter(Boolean))],
    ContainerIDs: [...new Set(storageEntries.map((s) => s.ContainerID).filter(Boolean))],
  };
};

export const isExpired = (req) => {
  if (
    req.Status === CHECKOUT_STATUS.COMPLETE.value ||
    req.Status === CHECKOUT_STATUS.CANCEL.value
  ) {
    return false;
  }

  const ms = req.PickupDate ? new Date(req.PickupDate).getTime() : null;

  return !!ms && ms <= Date.now();
};

export const CHECKOUT_REQUEST_DEFAULT_VALUES = {
  searchText: '',
  requestedDate: { from: '', to: '' },
  pickupDate: { from: '', to: '' },
  storageLocation: [],
  subLocation: [],
  container: [],
  requestBy: [],
  reason: [],
  pickupLocation: [],
};

export const uniqueSorted = (arr) => [...new Set(arr.filter(Boolean))].sort();

export const toCodeOptions = (values) =>
  uniqueSorted(values).map((v) => ({
    Code: v,
    Description: v,
  }));
